import { useState, useEffect, useMemo } from "react";
import { activityStore, fileStore, type Activity, type ActivityStatus, type ActivitySummary } from "../utils/dataStore";
import { useAdminData } from "../AdminDataContext";
import { apiDelete, apiPost, apiPut } from "../../../api/http";
import { activityToApiBody } from "../../../api/loadDataStore";
import ActivityFormModal, { type ActivityFormData, emptyForm } from "./ActivityFormModal";
import ActivitySummaryModal from "./ActivitySummaryModal";
import ActivityDetailModal from "./ActivityDetailModal";
import ActivityDashboard from "./ActivityDashboard";
import ActivityLeaderboardPanel from "./ActivityLeaderboardPanel";
import { basicInfoStore } from "../utils/basicInfoStore";
import {
  activityFormFromActivity,
  draftActivityFromForm,
  activityListStatusPill,
} from "../utils/activityFormModel";
import {
  adminActivityMatchesListFilter,
  countAdminActivitiesByListFilter,
  type AdminActivityListFilterKey,
} from "../../../utils/activityRegistrationWindow";
import { formatActivityInstantChinaDisplay, parseActivityInstantToUtcMs } from "../../../utils/activityCheckInDate";
import { reportError } from "../../../api/errors";
import {
  buildActivityCheckinPublicUrl,
  copyActivityCheckinPublicUrl,
  openActivityCheckinScreen,
} from "../../../utils/activityCheckinUrl";

export type { Activity, ActivityStatus };

interface Props {
}

const ADMIN_LIST_FILTERS: { key: AdminActivityListFilterKey; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "open", label: "可报名" },
  { key: "closed", label: "已截止" },
  { key: "draft", label: "草稿" },
  { key: "ended", label: "已结束" },
];

export const statusConfig: Record<ActivityStatus, { label: string; className: string }> = {
  ongoing: { label: "进行中",  className: "bg-green-50 text-green-600" },
  full:    { label: "已满",   className: "bg-red-50 text-red-600" },
  draft:   { label: "草稿",   className: "bg-gray-100 text-gray-500" },
  ended:   { label: "已结束", className: "bg-gray-100 text-gray-400" },
  pending: { label: "待审核", className: "bg-amber-50 text-amber-600" },
};

function getAllowedDocIds(): string[] {
  return fileStore
    .getAll()
    .filter((f) => f.type === "disclaimer" && f.status === "published")
    .map((f) => f.id);
}

function activityListFilterInput(a: Activity) {
  return {
    status: a.status,
    regStartDate: a.regStartDate ?? undefined,
    regEndDate: a.regEndDate ?? undefined,
    capacity: a.capacity,
    enrolled: a.enrolled,
  };
}

function labelForAdminListFilter(k: AdminActivityListFilterKey): string {
  return ADMIN_LIST_FILTERS.find((f) => f.key === k)?.label ?? "";
}

export default function ActivityManager(_: Props) {
  const { storeVersion, tenantLegacyId, reload } = useAdminData();
  const [activities, setActivities] = useState<Activity[]>(() => activityStore.getAll());
  const [shareOrgLine, setShareOrgLine] = useState(() => {
    const b = basicInfoStore.get();
    return (b.shortName || b.name || "球迷协会").trim() || "球迷协会";
  });
  const [shareLogo, setShareLogo] = useState(() => basicInfoStore.get().logoUrl?.trim() ?? "");

  useEffect(() => {
    return basicInfoStore.subscribe(() => {
      const b = basicInfoStore.get();
      setShareOrgLine((b.shortName || b.name || "球迷协会").trim() || "球迷协会");
      setShareLogo(b.logoUrl?.trim() ?? "");
    });
  }, []);

  useEffect(() => {
    setActivities(activityStore.getAll());
  }, [storeVersion]);

  const [activityDeleteTarget, setActivityDeleteTarget] = useState<Activity | null>(null);
  const [markEndedConfirm, setMarkEndedConfirm] = useState<string | null>(null);
  const [summaryActivity, setSummaryActivity] = useState<Activity | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editActivity, setEditActivity] = useState<Activity | null>(null);
  const [detailActivity, setDetailActivity] = useState<Activity | null>(null);
  const [showDashboard, setShowDashboard] = useState(false);
  const [rejectConfirm, setRejectConfirm] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState("");
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  const [listFilter, setListFilter] = useState<AdminActivityListFilterKey>("open");

  const filterCounts = useMemo(
    () => countAdminActivitiesByListFilter(activities.map(activityListFilterInput)),
    [activities],
  );

  const displayed = useMemo(() => {
    return activities
      .filter((a) => adminActivityMatchesListFilter(activityListFilterInput(a), listFilter))
      .sort((a, b) => {
        const ams = parseActivityInstantToUtcMs(a.date) ?? 0;
        const bms = parseActivityInstantToUtcMs(b.date) ?? 0;
        return bms - ams;
      });
  }, [activities, listFilter]);

  /* ── mark ended ── */
  const handleMarkEnded = async (id: string) => {
    const a = activities.find((x) => x.id === id);
    if (!a) return;
    try {
      await apiPut(`/api/tenants/${tenantLegacyId}/activities/${encodeURIComponent(id)}`, activityToApiBody({ ...a, status: "ended" }));
      await reload();
      setMarkEndedConfirm(null);
    } catch (e) {
      reportError(e, "操作失败");
    }
  };

  /* ── publish / unpublish ── */
  const handlePublish = async (id: string) => {
    const a = activities.find((x) => x.id === id);
    if (!a || a.status !== "draft") return;
    try {
      await apiPut(`/api/tenants/${tenantLegacyId}/activities/${encodeURIComponent(id)}`, activityToApiBody({ ...a, status: "ongoing" }));
      await reload();
    } catch (e) {
      reportError(e, "上架失败");
    }
  };
  const handleUnpublish = async (id: string) => {
    const a = activities.find((x) => x.id === id);
    if (!a || (a.status !== "ongoing" && a.status !== "full")) return;
    try {
      await apiPut(`/api/tenants/${tenantLegacyId}/activities/${encodeURIComponent(id)}`, activityToApiBody({ ...a, status: "draft" }));
      await reload();
    } catch (e) {
      reportError(e, "下架失败");
    }
  };

  /* ── save activity summary ── */
  const handleSaveSummary = async (summary: ActivitySummary) => {
    if (!summaryActivity) return;
    const merged: Activity = { ...summaryActivity, activitySummary: summary };
    try {
      await apiPut(
        `/api/tenants/${tenantLegacyId}/activities/${encodeURIComponent(summaryActivity.id)}`,
        activityToApiBody(merged),
      );
      await reload();
    } catch (e) {
      reportError(e, "保存失败");
    }
  };

  /* ── create / edit ── */
  const formFromActivity = (a: Activity): ActivityFormData => activityFormFromActivity(a);

  const handleCreate = async (data: ActivityFormData) => {
    const draftActivity = { ...draftActivityFromForm(data, null, getAllowedDocIds()), status: "draft" as const };
    try {
      await apiPost(`/api/tenants/${tenantLegacyId}/activities`, activityToApiBody(draftActivity));
      await reload();
      setCreateOpen(false);
    } catch (e) {
      reportError(e, "创建失败");
    }
  };

  const handleEdit = async (data: ActivityFormData) => {
    if (!editActivity) return;
    let updated = draftActivityFromForm(data, editActivity, getAllowedDocIds());
    if (updated.enrolled >= updated.capacity && updated.status === "ongoing") {
      updated = { ...updated, status: "full" };
    } else if (updated.enrolled < updated.capacity && updated.status === "full") {
      updated = { ...updated, status: "ongoing" };
    }
    try {
      await apiPut(
        `/api/tenants/${tenantLegacyId}/activities/${encodeURIComponent(editActivity.id)}`,
        activityToApiBody(updated),
      );
      await reload();
    } catch (e) {
      reportError(e, "保存失败");
    }
    setEditActivity(null);
  };

  const confirmDeleteActivity = async () => {
    if (!activityDeleteTarget) return;
    const id = activityDeleteTarget.id;
    try {
      await apiDelete(`/api/tenants/${tenantLegacyId}/activities/${encodeURIComponent(id)}`);
      await reload();
    } catch (e) {
      reportError(e, "删除失败");
    }
    setActivityDeleteTarget(null);
  };

  const canDeleteActivity = (a: Activity) =>
    a.status === "draft" || a.status === "pending" || a.status === "ended";

  const draftCount = filterCounts.draft;

  /* ── approve / reject pending ── */
  const handleApprove = async (id: string) => {
    const a = activities.find((x) => x.id === id);
    if (!a || a.status !== "pending") return;
    const approvedAt = new Date().toISOString().slice(0, 16).replace("T", " ");
    try {
      await apiPut(
        `/api/tenants/${tenantLegacyId}/activities/${encodeURIComponent(id)}`,
        activityToApiBody({ ...a, status: "ongoing", approvedAt }),
      );
      await reload();
    } catch (e) {
      reportError(e, "操作失败");
    }
  };

  const handleRejectActivity = async (id: string, note: string) => {
    const a = activities.find((x) => x.id === id);
    if (!a || a.status !== "pending") return;
    try {
      await apiPut(
        `/api/tenants/${tenantLegacyId}/activities/${encodeURIComponent(id)}`,
        activityToApiBody({ ...a, status: "draft", rejectionNote: note }),
      );
      await reload();
    } catch (e) {
      reportError(e, "操作失败");
    }
    setRejectConfirm(null);
    setRejectNote("");
  };

  const [shareActivity, setShareActivity] = useState<Activity | null>(null);
  const [shareSharedTo, setShareSharedTo] = useState<string | null>(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [activityCheckinToast, setActivityCheckinToast] = useState<string | null>(null);

  const handleShareTo = (target: string) => {
    setShareSharedTo(target);
    setTimeout(() => setShareSharedTo(null), 2000);
  };

  const handleShareCopyLink = () => {
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  };

  const flashActivityCheckinToast = (msg: string) => {
    setActivityCheckinToast(msg);
    setTimeout(() => setActivityCheckinToast(null), 2200);
  };

  const handleCopyActivityCheckinH5 = async (activityId: string) => {
    const ok = await copyActivityCheckinPublicUrl(activityId, tenantLegacyId);
    if (ok) {
      flashActivityCheckinToast("已复制签到动态码 H5 地址");
      return;
    }
    try {
      const url = buildActivityCheckinPublicUrl(activityId, tenantLegacyId);
      window.prompt("复制失败，请手动全选复制：", url);
    } catch (e) {
      reportError(e, "复制签到链接失败");
    }
  };

  // 会员报名 Excel 下载入口在活动详情弹窗；现场签到动态码 H5 仍通过列表/详情入口提供。

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900">活动管理</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              共 {activities.length} 个活动
              {draftCount > 0 && <span className="ml-2 text-amber-600">· {draftCount} 个草稿待发布</span>}
              {activities.filter((a) => a.status === "pending").length > 0 && (
                <span className="ml-2 text-orange-600">· {activities.filter((a) => a.status === "pending").length} 个待审核</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowDashboard(!showDashboard)}
              className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-xl cursor-pointer transition-colors whitespace-nowrap border ${showDashboard ? "bg-teal-50 text-teal-700 border-teal-200" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`}
            >
              <i className="ri-bar-chart-2-line"></i> 数据看板
            </button>
            <button
              onClick={() => setShowLeaderboard(!showLeaderboard)}
              className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-xl cursor-pointer transition-colors whitespace-nowrap border ${showLeaderboard ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`}
            >
              <i className="ri-trophy-line"></i> 积分排行
            </button>
            <button
              onClick={() => setCreateOpen(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white text-xs font-medium rounded-xl cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-add-line"></i> 创建活动
            </button>
          </div>
        </div>

        {/* Dashboard Panel */}
        {showDashboard && (
          <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/50">
            <ActivityDashboard tenantLegacyId={tenantLegacyId} activities={activities} />
          </div>
        )}

        {/* Leaderboard Panel */}
        {showLeaderboard && <ActivityLeaderboardPanel />}

        {/* Filter Bar */}
        <div className="px-6 py-3 bg-gray-50/60 border-b border-gray-100 overflow-x-auto">
          <div className="flex items-center gap-2 flex-nowrap min-w-max">
            {ADMIN_LIST_FILTERS.map(({ key, label }) => {
              const on = listFilter === key;
              const count = key === "all" ? undefined : filterCounts[key];
              return (
                <button
                  key={key}
                  onClick={() => setListFilter(key)}
                  className={`px-3.5 py-1.5 rounded-full text-xs cursor-pointer transition-all whitespace-nowrap ${on ? "bg-red-600 text-white font-medium shadow-sm" : "bg-white text-gray-500 border border-gray-200 hover:border-gray-300 hover:text-gray-700"}`}
                >
                  {label}
                  {count !== undefined ? (
                    <span className={`ml-1 font-bold ${on ? "text-white/75" : "text-gray-400"}`}>{count}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        {/* Activity Card List */}
        <div className="p-4 space-y-3">
          {displayed.length === 0 ? (
            <div className="py-14 text-center text-gray-400">
              <i className="ri-calendar-event-line text-4xl text-gray-200 block mb-2"></i>
              <p className="text-sm">
                {listFilter === "all" ? "暂无活动，点击创建" : `暂无${labelForAdminListFilter(listFilter)}活动`}
              </p>
            </div>
          ) : (
            displayed.map((a) => {
              const sc = activityListStatusPill(a.status);
              const fill = a.capacity > 0 ? Math.min(100, Math.round((a.enrolled / a.capacity) * 100)) : 0;
              const st = String(a.status || "").toLowerCase();
              const showFooter = st === "ongoing" || st === "full" || st === "draft";
              return (
                <div
                  key={a.id}
                  className={`bg-white rounded-2xl border border-gray-100 overflow-hidden transition-colors hover:border-gray-200 ${a.status === "ended" ? "opacity-75" : ""}`}
                >
                  <button
                    type="button"
                    onClick={() => setDetailActivity(a)}
                    className="w-full text-left cursor-pointer"
                  >
                    {a.coverUrl?.trim() ? (
                      <div className="w-full h-28 overflow-hidden bg-gray-100">
                        <img src={a.coverUrl.trim()} alt="" className="w-full h-full object-cover object-top" />
                      </div>
                    ) : null}
                    <div className="p-4">
                      <div className="flex items-start gap-3">
                        {!a.coverUrl?.trim() ? (
                          <div className="w-10 h-10 rounded-xl bg-red-50 text-red-500 flex items-center justify-center flex-shrink-0">
                            <i className="ri-calendar-event-line text-lg"></i>
                          </div>
                        ) : null}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <p className="text-sm font-bold text-gray-900 line-clamp-1">{a.title}</p>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${sc.className}`}>
                              {sc.label}
                            </span>
                            {a.lotteryEnabled ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-800 border border-amber-100">
                                抽奖
                              </span>
                            ) : null}
                          </div>
                          <p className="text-xs text-gray-400">
                            {formatActivityInstantChinaDisplay(a.date)}
                            <span className="text-gray-200 mx-1">·</span>
                            {(a.location || "待定").toString()}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden flex-1">
                              <div
                                className={`h-full rounded-full ${a.enrolled >= a.capacity ? "bg-red-500" : fill >= 80 ? "bg-amber-500" : "bg-green-500"}`}
                                style={{ width: `${fill}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-400 whitespace-nowrap">{a.enrolled}/{a.capacity}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>

                  {showFooter ? (
                    <div className="px-4 pb-4">
                      <div className="flex items-center gap-2">
                        {(st === "ongoing" || st === "full") && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleUnpublish(a.id);
                            }}
                            className="flex-1 flex items-center justify-center py-2.5 rounded-xl bg-gray-100 text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors whitespace-nowrap"
                          >
                            下架
                          </button>
                        )}
                        {st === "draft" && (
                          <>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditActivity(a);
                              }}
                              className="flex-1 flex items-center justify-center py-2.5 rounded-xl border border-gray-200 bg-white text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap"
                            >
                              编辑
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActivityDeleteTarget(a);
                              }}
                              className="flex-1 flex items-center justify-center py-2.5 rounded-xl bg-red-50 text-xs font-semibold text-red-600 cursor-pointer hover:bg-red-100 transition-colors whitespace-nowrap"
                            >
                              删除
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Activity Detail Modal */}
      {detailActivity && (
        <ActivityDetailModal
          activity={detailActivity}
          tenantLegacyId={tenantLegacyId}
          onClose={() => setDetailActivity(null)}
          onApprove={(id) => { void handleApprove(id); setDetailActivity(null); }}
          onReject={(id, note) => { void handleRejectActivity(id, note); setDetailActivity(null); }}
        />
      )}

      {activityCheckinToast ? (
        <div className="fixed bottom-6 left-1/2 z-[80] -translate-x-1/2 rounded-full bg-gray-900 px-4 py-2.5 text-sm text-white shadow-lg">
          {activityCheckinToast}
        </div>
      ) : null}

      {/* Inline Reject Confirm */}
      {rejectConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl p-6 w-[400px]">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-close-circle-line text-red-500 text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-800 mb-1">退回修改</h3>
                <p className="text-sm text-gray-500 leading-relaxed">请说明需要修改的内容，活动将退回草稿状态。</p>
              </div>
            </div>
            <div className="mb-5">
              <label className="block text-xs font-semibold text-gray-600 mb-2">退回原因 *</label>
              <textarea
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder="如：活动日期与其他活动冲突、地点需要确认等..."
                maxLength={200}
                rows={3}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:border-red-400"
              />
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setRejectConfirm(null); setRejectNote(""); }} className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={() => rejectNote.trim() && void handleRejectActivity(rejectConfirm, rejectNote)} disabled={!rejectNote.trim()} className="flex-1 py-2.5 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer disabled:opacity-50 whitespace-nowrap">确认退回</button>
            </div>
          </div>
        </div>
      )}

      {/* Activity Summary Modal */}
      {summaryActivity && <ActivitySummaryModal activity={summaryActivity} onSave={handleSaveSummary} onClose={() => setSummaryActivity(null)} />}

      {/* Create Modal */}
      {createOpen && (
        <ActivityFormModal mode="create" initialData={{ ...emptyForm }} onSubmit={handleCreate} onClose={() => setCreateOpen(false)} />
      )}

      {/* Edit Modal */}
      {editActivity && (
        <ActivityFormModal
          mode="edit"
          initialData={formFromActivity(editActivity)}
          onlinePayAmountsLocked={
            Boolean(editActivity.onlinePayAmountsLocked) || Number(editActivity.enrolled ?? 0) > 0
          }
          onSubmit={handleEdit}
          onClose={() => setEditActivity(null)}
        />
      )}

      {/* Delete activity */}
      {activityDeleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl p-6 w-96">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-delete-bin-line text-red-500 text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-800 mb-1">删除活动？</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  将永久删除「{activityDeleteTarget.title}」，且不可恢复。
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setActivityDeleteTarget(null)}
                className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                type="button"
                onClick={() => void confirmDeleteActivity()}
                className="flex-1 py-2.5 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer whitespace-nowrap"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark Ended Confirm */}
      {markEndedConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl p-6 w-96">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-flag-line text-amber-500 text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-800 mb-1">标记活动为已结束？</h3>
                <p className="text-sm text-gray-500 leading-relaxed">标记后该活动将从进行中状态变为「已结束」，且<span className="text-amber-600 font-medium">无法撤销</span>，请确认活动已实际结束。</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setMarkEndedConfirm(null)} className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={() => void handleMarkEnded(markEndedConfirm)} className="flex-1 py-2.5 text-sm text-white bg-gray-700 rounded-xl hover:bg-gray-800 cursor-pointer whitespace-nowrap"><i className="ri-flag-line mr-1.5"></i> 确认标记结束</button>
            </div>
          </div>
        </div>
      )}

      {/* WeChat Share Modal */}
      {shareActivity && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl w-[480px] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <i className="ri-wechat-fill text-green-500 text-lg"></i>
                <h3 className="font-bold text-gray-800">分享至微信用户</h3>
              </div>
              <button onClick={() => setShareActivity(null)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="p-6">
              {/* Share card preview */}
              <div className="bg-gray-50 rounded-2xl p-4 mb-5 flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-gray-200">
                  {shareActivity.coverUrl ? (
                    <img src={shareActivity.coverUrl} alt="" className="w-full h-full object-cover object-top" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-red-50">
                      <i className="ri-football-line text-red-400 text-2xl"></i>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900 line-clamp-2 leading-snug">{shareActivity.title}</p>
                  <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                    <i className="ri-map-pin-line text-xs"></i>{shareActivity.location}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                    <i className="ri-calendar-line text-xs"></i>{shareActivity.date}
                  </p>
                  <div className="flex items-center gap-1.5 mt-1">
                    {shareLogo ? (
                      <img src={shareLogo} alt="" className="w-3 h-3 object-contain rounded-full" />
                    ) : (
                      <i className="ri-football-line text-[10px] text-gray-400" aria-hidden />
                    )}
                    <span className="text-[10px] text-gray-400">{shareOrgLine}</span>
                  </div>
                </div>
              </div>

              {/* Share options */}
              <p className="text-xs font-semibold text-gray-500 mb-3">选择分享方式</p>
              <div className="flex items-center gap-6">
                <button onClick={() => handleShareTo("friend")} className="flex flex-col items-center gap-2 cursor-pointer group">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all ${shareSharedTo === "friend" ? "bg-green-500" : "bg-green-50 group-hover:bg-green-100"}`}>
                    {shareSharedTo === "friend" ? <i className="ri-check-line text-white text-2xl"></i> : <i className="ri-wechat-fill text-2xl" style={{ color: "#07C160" }}></i>}
                  </div>
                  <span className="text-xs text-gray-600">{shareSharedTo === "friend" ? "已发送" : "微信好友"}</span>
                </button>
                <button onClick={() => handleShareTo("moments")} className="flex flex-col items-center gap-2 cursor-pointer group">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all ${shareSharedTo === "moments" ? "bg-green-500" : "bg-green-50 group-hover:bg-green-100"}`}>
                    {shareSharedTo === "moments" ? <i className="ri-check-line text-white text-2xl"></i> : <i className="ri-wechat-2-fill text-2xl" style={{ color: "#07C160" }}></i>}
                  </div>
                  <span className="text-xs text-gray-600">{shareSharedTo === "moments" ? "已分享" : "朋友圈"}</span>
                </button>
                <button onClick={handleShareCopyLink} className="flex flex-col items-center gap-2 cursor-pointer group">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all ${shareCopied ? "bg-green-500" : "bg-gray-100 group-hover:bg-gray-200"}`}>
                    {shareCopied ? <i className="ri-check-line text-white text-2xl"></i> : <i className="ri-links-line text-gray-500 text-2xl"></i>}
                  </div>
                  <span className="text-xs text-gray-600">{shareCopied ? "已复制" : "复制链接"}</span>
                </button>
                <button className="flex flex-col items-center gap-2 cursor-pointer group">
                  <div className="w-14 h-14 rounded-2xl bg-gray-100 group-hover:bg-gray-200 flex items-center justify-center transition-all">
                    <i className="ri-qr-code-line text-gray-500 text-2xl"></i>
                  </div>
                  <span className="text-xs text-gray-600">二维码</span>
                </button>
              </div>
            </div>

            <div className="px-6 pb-5">
              <button onClick={() => setShareActivity(null)} className="w-full py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm cursor-pointer hover:bg-gray-200 transition-colors whitespace-nowrap">关闭</button>
            </div>
          </div>
        </div>
      )}

      {/* Export CSV Confirm */}
    </div>
  );
}
